<template>
  <div class="flex h-full flex-col">
    <div class="overflow-y-auto px-6">
      <PAlert v-if="hasErrorSending" type="error" class="mt-6">
        <span class="text-xs font-bold">Failed to submit ticket.</span>
        <span class="text-xs font-medium">Please try again.</span>
      </PAlert>
      <div class="py-6">
        <h3 class="text-2xl font-semibold leading-7">{{ title }}</h3>
      </div>
      <div class="mb-6">
        <p class="text-p-gray-40">{{ descriptionText }}</p>
      </div>
      <div class="mb-6">
        <HelpFormInput
          v-model="form.subject"
          mode="PInput"
          label="Subject"
          required
          :placeholder="subjectPlaceholder"
          maxlength="100"
          :error-msg="validationErrors.subject"
        />
      </div>
      <div class="mb-6">
        <HelpFormInput
          v-model="form.description"
          mode="PTextarea"
          label="Description"
          required
          :placeholder="descriptionPlaceholder"
          maxlength="2000"
          rows="7"
          :error-msg="validationErrors.description"
        />
      </div>
      <PFileUpload
        v-model="filesTobeUploaded"
        :multiple="true"
        :file-types="acceptedFileTypes"
        :max-size-in-bytes="50000000"
        label="Attach files (optional)"
        class="mb-6"
        data-testid="help-file-upload"
      />
    </div>
    <div class="mt-auto border-t border-p-gray-30 p-6 text-right">
      <PBtn type="primary-link" :disabled="isSending" class="mr-2" @click="cancel">Cancel</PBtn>
      <PBtn :loading="isSending" @click="createNewTicket">Submit Ticket</PBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PAlert, PBtn, PFileUpload } from '@pequity/squirrel';
import { computed, ref } from 'vue';
import * as yup from 'yup';
import { useValidation } from '@/composables/useValidation';
import { createTicket } from '@/help/api/help';
import HelpFormInput from '@/help/components/help-form-input.vue';
import { acceptedFileTypes, type CreateTicketPayload } from '@/help/types';

defineOptions({
  name: 'HelpForm',
});

type Props = {
  mode?: string;
};

const props = withDefaults(defineProps<Props>(), {
  mode: '',
});

const schema = yup.object().shape({
  subject: yup.string().max(100).required('Subject is required'),
  description: yup.string().max(2000).required('Description is required'),
});

const emit = defineEmits<{
  cancel: [];
  error: [];
  'ticket-created': [boolean];
}>();

// Data
const form = ref({
  subject: '',
  description: '',
});
const hasErrorSending = ref(false);
const isSending = ref(false);
const filesTobeUploaded = ref<File[]>([]);
const { validationValidate, validationScrollToError, validationErrors } = useValidation();

// Computed
const subjectPlaceholder = computed(() => {
  return props.mode === 'support' ? 'E.g. I’m having trouble adding a user' : 'E.g. How to benchmark a role';
});

const descriptionPlaceholder = computed(() => {
  return props.mode === 'support'
    ? 'E.g. I am encountering an error when assigning specific permissions for a a new user.'
    : 'E.g. I’m not sure exactly how to benchmark a QA Engineering role and was hoping you could advise';
});

const descriptionText = computed(() => {
  return props.mode === 'support'
    ? `Tell us a little more about your situation and we’ll get in touch as soon as
    possible.`
    : `Need to reach our compensation experts? Tell us a little more about your situation and we’ll get in touch as soon as possible.`;
});

const title = computed(() => {
  return props.mode === 'support' ? 'Contact Pequity’s Support Team' : 'Ask a Compensation Expert';
});

// Methods
const cancel = () => {
  emit('cancel');
};

const createNewTicket = async () => {
  const res = await validationValidate({ schema, data: form });

  if (res !== true) {
    validationScrollToError();
    return;
  }

  hasErrorSending.value = false;
  isSending.value = true;

  const payload = new FormData() as CreateTicketPayload;
  payload.append('subject', form.value.subject);
  payload.append('comment', form.value.description);
  payload.append('tag', props.mode);

  filesTobeUploaded.value.forEach((file) => payload.append('files', file));

  const { error } = await createTicket(payload);

  if (!error) {
    isSending.value = false;
    emit('ticket-created', true);
  } else {
    hasErrorSending.value = true;
    isSending.value = false;
    emit('error');
  }
};
</script>
